// SUB-OFFICE 7 — THE VERTICAL-CORE CONTROLLER (increment M-B). The seam between
// the FSM (game.ts) and the vertical circulation core: it owns entering the
// lift lobby from Intake, the in-lobby interaction loop (call plates, stair,
// dead escalator, mezzanine door, the exit back to Intake), the cab rides
// down to the descent-vestibule stub and back, the per-zone light grade
// (including the Q1 phosphor->bone fade the stub demonstrates), and the FSM
// bookkeeping. It is the M-B twin of ArrivalController and plugs into the same
// boot deps.
//
// It drives the ZoneManager (the N-zone spine) for build/dispose + collision
// swap, and the CabMask for the masked transitions. All signage/voice is
// verbatim from content/machine-floor.json verticalCore.

import { Color, DirectionalLight, FogExp2, HemisphereLight, type Scene } from 'three';
import { ZoneManager } from './manager';
import { CabMask } from './cab';
import { buildVerticalCore, type VerticalCoreBuild } from './verticalcore';
import { buildMachineFloor, type MachineFloorBuild } from './machine';
import { MachineFloorController } from './machinecontroller';
import type { ZoneHotspot } from './types';
import type { SceneManager, ViewMode } from '../../render/scene';
import type { FirstPersonView } from '../../render/fpview';
import type { Player } from '../../entities/player';
import type { Fsm } from '../../state';
import type { DomUi } from '../../ui/dom';
import type { EventBus } from '../../entities/entities';
import machineFloor from '../../../content/machine-floor.json';

export interface VerticalCoreDeps {
  scene: Scene;
  sceneMgr: SceneManager;
  player: Player;
  fpv: FirstPersonView;
  fsm: Fsm;
  dom: DomUi;
  bus: EventBus;
  applyView: (v: ViewMode) => void;
  setInteriorVisible: (on: boolean) => void;
  reducedMotion: boolean;
}

interface SavedGrade {
  fogColor: Color;
  fogDensity: number;
  background: Color | null;
  hemiIntensity: number;
  hemiSky: Color;
  hemiGround: Color;
  keyIntensity: number;
  keyColor: Color;
}

const LOBBY_FOG = 0xbdb7a6;   // institutional cold bone

// THE MACHINE FLOOR canonical INVERTED grade (art-direction; hemisphere poles
// SWAPPED — bright phosphor GROUND, near-black SKY). fog is carbon steeped in
// phosphor. These supersede the retired stub's sky-bright descent green.
const MF_GRADE: GradeSpec = { fog: 0x0e2417, density: 0.055, hemiInt: 0.55, hemiSky: 0x0c130e, hemiGround: 0x3f8a4d, keyInt: 0.05, keyColor: 0x2f5f3a };
const LOBBY_GRADE: GradeSpec = { fog: 0xbdb7a6, density: 0.05, hemiInt: 0.5, hemiSky: 0xcbc4b2, hemiGround: 0x2a2620, keyInt: 0.2, keyColor: 0x8a8272 };

/** a full lighting grade (fog + hemisphere + key) — the unit startFullFade lerps. */
interface GradeSpec {
  fog: number; density: number; hemiInt: number; hemiSky: number; hemiGround: number; keyInt: number; keyColor: number;
}

export class VerticalCoreController {
  private mgr: ZoneManager;
  private cab: CabMask;
  private lobby: VerticalCoreBuild | null = null;
  private machineBuild: MachineFloorBuild | null = null;
  private machineCtl: MachineFloorController;
  private active = false;
  private saved: SavedGrade | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  private chyTimer = 0;
  private chyIdx = 0;
  private armedHotspot: string | null = null;
  private lineRot = 0;
  // Q1 full light-fade state (the phosphor<->bone wash): lerps fog AND the
  // hemisphere poles (hemiGround is the dominant term) + key over `fadeDur`.
  private fadeT = 0;
  private fadeDur = 0;
  private fadeFromFog = new Color();
  private fadeToFog = new Color();
  private fadeHemi = false;
  private fFrom: GradeSpec = LOBBY_GRADE;
  private fTo: GradeSpec = LOBBY_GRADE;
  private fFromSky = new Color(); private fToSky = new Color();
  private fFromGround = new Color(); private fToGround = new Color();
  private fFromKey = new Color(); private fToKey = new Color();

  constructor(private deps: VerticalCoreDeps) {
    this.mgr = new ZoneManager({
      scene: deps.scene, player: deps.player, fpv: deps.fpv,
      applyView: deps.applyView, setInteriorVisible: deps.setInteriorVisible,
      reducedMotion: deps.reducedMotion,
    });
    this.cab = new CabMask(deps.reducedMotion);
    // the resident interior + the two streamed zones of the core
    this.mgr.register({
      id: 'floor7', residency: 'resident',
      spawns: { lift_return: { x: 28, z: -6, facing: Math.PI / 2 } }, // stepping back out of the lift into Intake
    });
    this.mgr.register({ id: 'vertical_core', residency: 'streamed', build: buildVerticalCore });
    // M-D: the cab descent now lands on the REAL Machine Floor (buildDescentStub
    // retired). One streamed record, full heap swap under the cab mask.
    this.mgr.register({ id: 'machine_floor', residency: 'streamed', build: buildMachineFloor });
    this.machineCtl = new MachineFloorController(
      { dom: deps.dom, bus: deps.bus, reducedMotion: deps.reducedMotion },
      machineFloor.machineFloor.directAddress,
    );
  }

  get isActive(): boolean { return this.active; }

  /** Q5 plumbing: arm/disarm the hidden mark that permits an unbidden stop.
   *  Exposed so Wave-2 (Aversion Protocol / Bureau Calendar) can light it. */
  armUnbiddenStop(on: boolean): void { this.cab.arm(on); }

  /** Enter the lift lobby from Intake (called on E at the interior lift door). */
  enter(): void {
    if (this.active) return;
    this.saveGrade();
    this.lobby = this.mgr.travel('vertical_core', 'from_intake') as VerticalCoreBuild;
    this.applyGrade(LOBBY_FOG, 0.05, 0.5, 0xcbc4b2, 0x2a2620, 0.2, 0x8a8272);
    this.deps.fsm.to('LIFT_LOBBY');
    this.chyTimer = 0; this.chyIdx = 0; this.lineRot = 0;
    this.printChyron();
    this.installKeys();
    this.active = true;
  }

  update(dt: number): void {
    if (!this.active) return;
    // full light-fade (Q1) easing — fog + hemisphere poles + key in lockstep
    if (this.fadeDur > 0) {
      this.fadeT += dt;
      const k = Math.min(1, this.fadeT / this.fadeDur);
      const fog = this.deps.scene.fog as FogExp2 | null;
      if (fog) fog.color.copy(this.fadeFromFog).lerp(this.fadeToFog, k);
      if (this.fadeHemi) {
        for (const o of this.deps.scene.children) {
          if (o instanceof HemisphereLight) {
            o.intensity = this.fFrom.hemiInt + (this.fTo.hemiInt - this.fFrom.hemiInt) * k;
            o.color.copy(this.fFromSky).lerp(this.fToSky, k);
            o.groundColor.copy(this.fFromGround).lerp(this.fToGround, k); // the big term: phosphor<->bone
          } else if (o instanceof DirectionalLight) {
            o.intensity = this.fFrom.keyInt + (this.fTo.keyInt - this.fFrom.keyInt) * k;
            o.color.copy(this.fFromKey).lerp(this.fToKey, k);
          }
        }
      }
      if (k >= 1) { this.fadeDur = 0; this.fadeHemi = false; }
    }
    // tick the DirectVoice caption every active frame (fades the withdrawal +
    // forgetting lines during and after the ride-up, off the floor).
    this.machineCtl.tickVoice(dt);
    const here = this.mgr.active;
    if (here === 'vertical_core' && this.lobby) {
      this.lobby.chyron.update(dt);
      this.lobby.tickRiser(dt);
      this.hotspotScan(this.lobby.hotspots);
    } else if (here === 'machine_floor' && this.machineBuild) {
      const b = this.machineBuild;
      b.chyron.update(dt);
      b.tickLED(dt); b.tickCoolant(dt); b.tickShimmer(dt);
      const p = this.deps.player.pos;
      this.machineCtl.update(dt, p.x, p.z);
      if (!this.cab.riding && !this.machineCtl.isBusy) this.hotspotScan(b.hotspots);
    }
    // ambient chyron cadence
    if (!this.cab.riding) {
      this.chyTimer += dt;
      if (this.chyTimer > 5.2) { this.chyTimer = 0; this.printChyron(); }
    }
  }

  // --- interaction -----------------------------------------------------------

  private hotspotScan(hotspots: ZoneHotspot[]): void {
    const p = this.deps.player.pos;
    let best: ZoneHotspot | null = null;
    let bestD = Infinity;
    for (const h of hotspots) {
      if (h.enabled && !h.enabled()) continue; // duty-gated / locked reads no prompt
      const dx = h.x - p.x, dz = h.z - p.z;
      const d2 = dx * dx + dz * dz;
      if (d2 <= h.reach * h.reach && d2 < bestD) { bestD = d2; best = h; }
    }
    this.armedHotspot = best?.id ?? null;
    this.deps.dom.setPrompt(best ? best.prompt : null);
  }

  private installKeys(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if ((e.code === 'KeyE' || e.code === 'Space') && !e.repeat && this.active && !this.cab.riding) {
        e.preventDefault();
        this.onInteract();
      }
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  private onInteract(): void {
    const id = this.armedHotspot;
    if (!id) return;
    const c = machineFloor.verticalCore;
    // On the Machine Floor, delegate to its controller (maintenance stations,
    // the diegetic verdict, the descent script). The cab-up return + the
    // duty-gated ascent stair are handled here.
    if (this.mgr.active === 'machine_floor') {
      if (id === 'cab_up') { void this.rideUp(); return; }
      if (id === 'ascent_stair') {
        // duty-gated: locked line until the round is discharged (stair route M-C)
        const sig = machineFloor.machineFloor.rackHallSignage;
        this.deps.dom.say(this.machineCtl.dutyDischarged ? machineFloor.machineFloor.wayfinding[4] : sig[16], 6);
        return;
      }
      if (this.machineCtl.interact(id)) return;
    }
    switch (id) {
      case 'call_plate': void this.rideDown(); return;
      case 'call_plate_up': void this.rideUp(); return;
      case 'lift_exit': this.leave(); return;
      case 'dead_plate': this.deps.dom.say(c.elevatorLines[9], 6); return; // "YOU MAY PRESS THE BUTTON AGAIN. IT WILL CHANGE NOTHING…"
      case 'stair': this.deps.dom.say(rot(c.stairLines, this.lineRot++), 6); return;
      case 'escalator': this.deps.dom.say(rot(c.escalatorLines, this.lineRot++), 6); return;
      case 'mezzanine': this.deps.dom.say(rot(c.mezzanineDoor, this.lineRot++), 6); return;
    }
  }

  // --- the rides -------------------------------------------------------------

  private async rideDown(): Promise<void> {
    const c = machineFloor.verticalCore;
    this.lobby?.setFloorFlap('-1');
    this.deps.fsm.to('ELEVATOR');
    await this.cab.ride({
      fromLabel: '0', toLabel: '-1', descend: true,
      onSwap: () => {
        this.machineBuild = this.mgr.travel('machine_floor', 'from_cab') as MachineFloorBuild;
        this.lobby = null; // disposed under the mask (full heap swap, Q1)
      },
    });
    // arrived on FLOOR -1. The cab masked the swap, so the canonical INVERTED
    // grade lands directly (hemisphere poles swapped — bright phosphor GROUND,
    // near-black SKY). No fade on the masked route; the fade is the stair's.
    this.applyGrade(MF_GRADE.fog, MF_GRADE.density, MF_GRADE.hemiInt, MF_GRADE.hemiSky, MF_GRADE.hemiGround, MF_GRADE.keyInt, MF_GRADE.keyColor);
    this.deps.fsm.to('DESCENT_STUB'); // reused FSM label: "on the streamed sub-floor"
    this.chyTimer = 0; this.chyIdx = 0;
    const b = this.machineBuild;
    if (b) {
      b.chyron.print('MAINTENANCE. PROCEED.'); // the chyron drops its manners
      this.machineCtl.onArrive(b, 'cab');       // DirectVoice opens (THESIS 1)
    }
    void c;
  }

  private async rideUp(): Promise<void> {
    const c = machineFloor.verticalCore;
    this.machineCtl.onDepart(); // withdrawal + forgetting DirectVoice lines
    this.deps.fsm.to('ELEVATOR');
    await this.cab.ride({
      fromLabel: '-1', toLabel: '0', descend: false,
      onSwap: () => {
        this.lobby = this.mgr.travel('vertical_core', 'from_cab') as VerticalCoreBuild;
        this.machineBuild = null;
      },
    });
    // the phosphor->bone DRAIN (Q1): climbing out of the god's body washes the
    // green from your eyes. Full fade (fog+hemi+key) over 1.6s (0.2s reduced).
    this.applyGrade(MF_GRADE.fog, MF_GRADE.density, MF_GRADE.hemiInt, MF_GRADE.hemiSky, MF_GRADE.hemiGround, MF_GRADE.keyInt, MF_GRADE.keyColor);
    this.startFullFade(MF_GRADE, LOBBY_GRADE, this.deps.reducedMotion ? 0.2 : 1.6);
    this.deps.fsm.to('LIFT_LOBBY');
    this.chyTimer = 0; this.chyIdx = 0;
    this.lobby?.chyron.print(c.elevatorLines[10]); // "ASCENDING. YOU ARE BEING RETURNED TO THE PAPER FLOORS…"
  }

  /** Back to Intake (E at the lobby exit). Restores the resident floor fully. */
  private leave(): void {
    this.mgr.travel('floor7', 'lift_return');
    this.restoreGrade();
    this.deps.fsm.to('ROAM');
    this.teardown();
    this.deps.dom.say(machineFloor.verticalCore.elevatorLines[10], 4);
  }

  private teardown(): void {
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler); this.keyHandler = null; }
    this.armedHotspot = null;
    this.deps.dom.setPrompt(null);
    this.lobby = null;
    this.machineBuild = null;
    this.machineCtl.dispose(); // clears the DirectVoice caption + resets the deck
    this.active = false;
    this.fadeDur = 0;
    this.fadeHemi = false;
  }

  // --- ambient chyron --------------------------------------------------------

  private printChyron(): void {
    const c = machineFloor.verticalCore;
    if (this.mgr.active === 'machine_floor' && this.machineBuild) {
      // the office pretence persists on the cornice: rack-hall signage, ALL CAPS
      this.machineBuild.chyron.print(rot(machineFloor.machineFloor.rackHallSignage, this.chyIdx++));
      return;
    }
    if (this.lobby) {
      // lobby ambiance: the elevator lines that read while WAITING
      const pool = [c.elevatorLines[0], c.elevatorLines[1], c.elevatorLines[3], c.elevatorLines[8], c.elevatorLines[4]];
      this.lobby.chyron.print(rot(pool, this.chyIdx++));
    }
  }

  // --- the light grade (per-zone; saved once on enter) -----------------------

  private saveGrade(): void {
    const scene = this.deps.scene;
    const fog = scene.fog as FogExp2 | null;
    const saved: SavedGrade = {
      fogColor: fog ? fog.color.clone() : new Color(0),
      fogDensity: fog ? fog.density : 0.012,
      background: scene.background instanceof Color ? scene.background.clone() : null,
      hemiIntensity: 0.85, hemiSky: new Color(), hemiGround: new Color(),
      keyIntensity: 0.55, keyColor: new Color(),
    };
    for (const o of scene.children) {
      if (o instanceof HemisphereLight) {
        saved.hemiIntensity = o.intensity; saved.hemiSky = o.color.clone(); saved.hemiGround = o.groundColor.clone();
      } else if (o instanceof DirectionalLight) {
        saved.keyIntensity = o.intensity; saved.keyColor = o.color.clone();
      }
    }
    this.saved = saved;
  }

  private applyGrade(
    fogHex: number, fogDensity: number, hemiIntensity: number, hemiSky: number,
    hemiGround: number, keyIntensity: number, keyColor: number
  ): void {
    const scene = this.deps.scene;
    const fog = scene.fog as FogExp2 | null;
    if (fog) { fog.color.setHex(fogHex).convertSRGBToLinear(); fog.density = fogDensity; }
    if (scene.background instanceof Color) scene.background.setHex(fogHex).convertSRGBToLinear();
    for (const o of scene.children) {
      if (o instanceof HemisphereLight) {
        o.intensity = hemiIntensity;
        o.color.setHex(hemiSky).convertSRGBToLinear();
        o.groundColor.setHex(hemiGround).convertSRGBToLinear();
      } else if (o instanceof DirectionalLight) {
        o.intensity = keyIntensity; o.color.setHex(keyColor).convertSRGBToLinear();
      }
    }
  }

  /**
   * Q1 (spec increment 2): the phosphor<->bone light-fade. Unlike the retired
   * stub's fog-only startFade, this lerps IN PARALLEL over `dur` the fog color
   * AND the hemisphere poles (hemiGround is the DOMINANT term — a fog-only fade
   * would leave the green lingering in material lighting) AND the key. This is
   * the primitive the UNMASKED conscription stair reuses (down: bone->phosphor
   * grow ~2.4s; up: phosphor->bone drain 1.6s). Exercised here on the cab
   * ride-up green-drain so it ships proven.
   */
  private startFullFade(from: GradeSpec, to: GradeSpec, dur: number): void {
    this.fFrom = from; this.fTo = to;
    this.fadeFromFog.setHex(from.fog).convertSRGBToLinear();
    this.fadeToFog.setHex(to.fog).convertSRGBToLinear();
    this.fFromSky.setHex(from.hemiSky).convertSRGBToLinear();
    this.fToSky.setHex(to.hemiSky).convertSRGBToLinear();
    this.fFromGround.setHex(from.hemiGround).convertSRGBToLinear();
    this.fToGround.setHex(to.hemiGround).convertSRGBToLinear();
    this.fFromKey.setHex(from.keyColor).convertSRGBToLinear();
    this.fToKey.setHex(to.keyColor).convertSRGBToLinear();
    // density is not lerped — snap it to the destination at the start
    const fog = this.deps.scene.fog as FogExp2 | null;
    if (fog) fog.density = to.density;
    this.fadeT = 0; this.fadeDur = dur; this.fadeHemi = true;
  }

  private restoreGrade(): void {
    const s = this.saved; if (!s) return;
    const scene = this.deps.scene;
    const fog = scene.fog as FogExp2 | null;
    if (fog) { fog.color.copy(s.fogColor); fog.density = s.fogDensity; }
    if (s.background && scene.background instanceof Color) scene.background.copy(s.background);
    for (const o of scene.children) {
      if (o instanceof HemisphereLight) {
        o.intensity = s.hemiIntensity; o.color.copy(s.hemiSky); o.groundColor.copy(s.hemiGround);
      } else if (o instanceof DirectionalLight) {
        o.intensity = s.keyIntensity; o.color.copy(s.keyColor);
      }
    }
    this.saved = null;
  }
}

/** rotate through a line pool by an ever-incrementing index. */
function rot(pool: string[], i: number): string { return pool[((i % pool.length) + pool.length) % pool.length]; }
